import { Dtos, FileType } from "@inrev/common";
import {
	useCompleteAgentIssuedBondForm,
	useFetchAdminBondIssuance,
} from "@portal/api/bond-request/admin";
import {
	AdminBondRequest,
	AdminUploadAgentIssuedBondFormFormData,
} from "@portal/api/bond-request/admin/types";
import {
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
	FormSection,
} from "@portal/components/form";
import {
	FormDatePicker,
	FormDropdown,
	FormInput,
	FormStateDropdown,
	FormYesNo,
} from "@portal/components/form/fields";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/ui/Button";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { NotificationCard } from "@portal/components/ui/NotificationCard";
import { useObligeeRoleOptions } from "@portal/hooks/useObligeeRoleOptions";
import { useEffect, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { NameSuffix, UploadFile } from "../../../types";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";

type AdminCompleteAgentIssuedBondFormFormProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response;
};

type InnerProps = AdminCompleteAgentIssuedBondFormFormProps & {
	bondIssuance: Dtos.SuretyBondIssuance.Get.Response;
};

const getDefaultFormData = (
	request: AdminCompleteAgentIssuedBondFormFormProps["request"],
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response,
): AdminUploadAgentIssuedBondFormFormData => ({
	suretyType: request.suretyType,
	contractSuretyType: request.contractSuretyType === "bid" ? "bid" : "final",
	cancellable:
		request.contractSuretyType === "bid" ? false : (bondForm.attributes.cancellable ?? ""),
	continuous: request.contractSuretyType === "bid" ? true : (bondForm.attributes.continuous ?? ""),
	bondForm: {
		data: {
			bondNumber: bondForm.fillData.bondNumber ?? "",
			effectiveDate: bondForm.fillData.effectiveDate ?? "",
			contractDate: "",
			contractDescription: request.project.name ?? "",
			bidDate: request.contractSuretyType === "bid" ? (request.bidDate ?? "") : "",
			projectDescription: request.project.name ?? "",
			obligees: [
				{
					name: "",
					address: {
						street: "",
						city: "",
						state: "",
						zip: "",
					},
					role: "",
				},
			],
			principalSigner: {
				firstName: "",
				lastName: "",
				suffix: "",
				title: "",
			},
		},
	},
});

const _AdminCompleteAgentIssuedBondFormForm = ({ request, bondForm, bondIssuance }: InnerProps) => {
	const obligeeRoleOptions = useObligeeRoleOptions();
	const formMethods = useForm<
		AdminUploadAgentIssuedBondFormFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		| Dtos.Admin.SuretyBondForm.Upload.AgentIssue.Request
		| Dtos.Admin.SuretyBondForm.Approve.AgentIssue.Request
	>({
		defaultValues: getDefaultFormData(request, bondForm),
		reValidateMode: "onBlur",
		resolver: (() => {
			if (!bondIssuance.pendingAgentIssueAdminReview) {
				return stripEmptyResolver(
					typedZodResolver(Dtos.Admin.SuretyBondForm.Upload.AgentIssue.Request.schema),
					false,
				);
			}
			return stripEmptyResolver(
				typedZodResolver(Dtos.Admin.SuretyBondForm.Approve.AgentIssue.Request.schema),
				false,
			);
		})(),
	});
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const widenedFormMethods: UseFormReturn<AdminUploadAgentIssuedBondFormFormData, any, any> =
		formMethods;
	const [bondFormFileUpload, setBondFormFileUpload] = useState<
		UploadFile<typeof FileType.filled_bond_form>[]
	>([]);

	const {
		completeAgentIssuedBondForm,
		completeAgentIssuedBondFormIsLoading,
		completeAgentIssuedBondFormIsSuccess,
		resetCompleteAgentIssuedBondForm,
	} = useCompleteAgentIssuedBondForm(
		request.id,
		request.suretyAccountId,
		!bondIssuance.pendingAgentIssueAdminReview,
	);

	useEffect(() => {
		if (completeAgentIssuedBondFormIsSuccess) {
			formMethods.setValue("bondForm.fileId", undefined);
			setBondFormFileUpload([]);
			resetCompleteAgentIssuedBondForm();
		}
	}, [completeAgentIssuedBondFormIsSuccess, resetCompleteAgentIssuedBondForm, formMethods]);

	const handleSubmit = (
		data:
			| Dtos.Admin.SuretyBondForm.Upload.AgentIssue.Request
			| Dtos.Admin.SuretyBondForm.Approve.AgentIssue.Request,
	) => {
		completeAgentIssuedBondForm(data);
	};

	return (
		<form className="flex flex-col space-y-[35px]">
			{bondIssuance.pendingAgentIssue && (
				<NotificationCard
					type="action"
					text="Provide the following details and upload a copy of the agent issued bond form"
				/>
			)}
			{bondIssuance.pendingAgentIssueAdminReview && (
				<NotificationCard
					type="action"
					text="Provide the following details to finalize the agent issued bond form"
				/>
			)}
			{bondForm.filledWithAdornmentsFileId &&
				!bondIssuance.pendingAgentIssue &&
				!bondIssuance.pendingAgentIssueAdminReview && (
					<NotificationCard
						type="action"
						text="Upload a new bond form to replace the existing one"
					/>
				)}
			<FormFieldGroup>
				<FormRow>
					<FormField condensed>
						<FormItemLabel required>Bond Number</FormItemLabel>
						<FormInput
							control={widenedFormMethods.control}
							name={`bondForm.data.bondNumber`}
							className="bg-white"
							compact
						/>
					</FormField>
					<FormField condensed>
						<FormItemLabel required>Effective Date</FormItemLabel>
						<FormDatePicker
							name="bondForm.data.effectiveDate"
							control={widenedFormMethods.control}
							compact
						/>
					</FormField>
					{(request.contractSuretyType === "final" ||
						request.contractSuretyType === "bid_to_final") && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Contract Date
							</FormItemLabel>
							<FormDatePicker
								control={widenedFormMethods.control}
								name={`bondForm.data.contractDate`}
								className="bg-white"
								compact
							/>
						</FormField>
					)}
					{request.contractSuretyType === "bid" && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Bid Date
							</FormItemLabel>
							<FormDatePicker
								control={widenedFormMethods.control}
								name={`bondForm.data.bidDate`}
								className="bg-white"
								compact
							/>
						</FormField>
					)}
				</FormRow>
				<FormField>
					<FormItemLabel required>Description</FormItemLabel>
					<span className="!my-[5px] text-[14px] text-tertiary italic">
						As it appears on the bond form
					</span>
					<FormInput
						control={widenedFormMethods.control}
						name={
							request.contractSuretyType === "bid"
								? `bondForm.data.projectDescription`
								: `bondForm.data.contractDescription`
						}
						className="bg-white"
						compact
					/>
				</FormField>
				{request.contractSuretyType !== "bid" && (
					<FormRow className="space-x-[50px] pt-[8px]">
						<FormField className="max-w-fit">
							<FormItemLabel required>Continuous</FormItemLabel>
							<FormYesNo name="continuous" control={widenedFormMethods.control} />
						</FormField>
						<FormField className="flex-2">
							<FormItemLabel required>Cancellable</FormItemLabel>
							<FormYesNo name="cancellable" control={widenedFormMethods.control} />
						</FormField>
					</FormRow>
				)}
			</FormFieldGroup>
			<FormSection header="Obligee">
				<FormFieldGroup condensed>
					<FormRow>
						<FormField condensed>
							<FormItemLabel condensed required>
								Name
							</FormItemLabel>
							<FormInput
								control={widenedFormMethods.control}
								name={`bondForm.data.obligees.0.name`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField condensed className="w-[50px] max-w-[225px]">
							<FormItemLabel condensed required>
								Role
							</FormItemLabel>
							<FormDropdown
								control={widenedFormMethods.control}
								name={"bondForm.data.obligees.0.role"}
								buttonClassName="bg-white"
								options={obligeeRoleOptions}
								placeholder="Select one"
								compact
							/>
						</FormField>
					</FormRow>
					<FormField condensed>
						<FormItemLabel condensed required>
							Street
						</FormItemLabel>
						<FormInput
							control={widenedFormMethods.control}
							name={`bondForm.data.obligees.0.address.street`}
							className="bg-white"
							compact
						/>
						<FormRow>
							<FormField condensed>
								<FormItemLabel condensed required>
									City
								</FormItemLabel>
								<FormInput
									control={widenedFormMethods.control}
									name={`bondForm.data.obligees.0.address.city`}
									className="bg-white"
									compact
								/>
							</FormField>
							<FormField condensed className="w-[100px] max-w-[100px]">
								<FormItemLabel condensed required>
									State
								</FormItemLabel>
								<FormStateDropdown
									control={widenedFormMethods.control}
									name={`bondForm.data.obligees.0.address.state`}
									buttonClassName="bg-white"
									optionsClassName="max-h-[230px] overflow-y-auto"
									compact
									abbreviate
								/>
							</FormField>
							<FormField condensed className="w-[100px] max-w-[100px]">
								<FormItemLabel condensed required>
									Zip
								</FormItemLabel>
								<FormInput
									control={widenedFormMethods.control}
									name={`bondForm.data.obligees.0.address.zip`}
									className="bg-white"
									compact
								/>
							</FormField>
						</FormRow>
					</FormField>
				</FormFieldGroup>
			</FormSection>
			<FormSection
				header="Signer"
				subHeader="If available"
				subHeaderClassName="text-tertiary italic"
			>
				<FormFieldGroup condensed>
					<FormRow>
						<FormField condensed>
							<FormItemLabel condensed>First Name</FormItemLabel>
							<FormInput
								control={widenedFormMethods.control}
								name={`bondForm.data.principalSigner.firstName`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField condensed>
							<FormItemLabel condensed>Last Name</FormItemLabel>
							<FormInput
								control={widenedFormMethods.control}
								name={`bondForm.data.principalSigner.lastName`}
								className="bg-white"
								compact
							/>
						</FormField>
						<FormField className="w-[100px] max-w-[100px]" condensed>
							<FormItemLabel condensed>Suffix</FormItemLabel>
							<FormDropdown
								control={widenedFormMethods.control}
								name="bondForm.data.principalSigner.suffix"
								options={Object.values(NameSuffix).map((suffix) => ({
									value: suffix,
									label: suffix,
								}))}
								buttonClassName="bg-white"
								compact
							/>
						</FormField>
					</FormRow>
					<FormField condensed>
						<FormItemLabel condensed>Title</FormItemLabel>
						<FormInput
							control={widenedFormMethods.control}
							name={`bondForm.data.principalSigner.title`}
							className="bg-white"
							compact
						/>
					</FormField>
				</FormFieldGroup>
			</FormSection>
			{!bondIssuance.pendingAgentIssueAdminReview && (
				<LocalFileUpload
					value={bondFormFileUpload}
					onChange={(files) => {
						setBondFormFileUpload(files);
						formMethods.setValue("bondForm.fileId", files[0]?.id);
					}}
					allowDelete={true}
					allowedTypesAndLabels={{
						[FileType.filled_bond_form]: "Filled Bond Form",
					}}
					allowedExtensions={["pdf"]}
					requiredTypes={[FileType.filled_bond_form]}
					maxTypesPerFile={1}
					maxFiles={1}
					hideChecklist={true}
					showErrors={formMethods.formState.errors.bondForm?.fileId !== undefined}
					analyticsContext="bond-attachments"
				/>
			)}
			<ConfirmationModalClickWrapper
				shouldConfirm={true}
				message="Are you sure?"
				onConfirm={formMethods.handleSubmit(handleSubmit)}
			>
				<Button
					color="primary"
					className="max-w-fit self-center px-[50px] !mb-[20px] !mt[45px]"
					filled
				>
					Submit
				</Button>
			</ConfirmationModalClickWrapper>
			{completeAgentIssuedBondFormIsLoading && <LoadingModal />}
		</form>
	);
};

export const AdminCompleteAgentIssuedBondFormForm = ({
	request,
	bondForm,
}: AdminCompleteAgentIssuedBondFormFormProps) => {
	const { bondIssuance } = useFetchAdminBondIssuance(request.suretyAccountId, request.id);

	if (
		bondIssuance &&
		bondIssuance.method === "agent" &&
		!bondIssuance.pendingBondFormReview &&
		(bondIssuance.pendingAgentIssue ||
			bondIssuance.pendingAgentIssueAdminReview ||
			bondForm.filledWithAdornmentsFileId)
	) {
		return (
			<_AdminCompleteAgentIssuedBondFormForm
				request={request}
				bondForm={bondForm}
				bondIssuance={bondIssuance}
			/>
		);
	}
	return <></>;
};
